import { getErrorLocale, type ErrorLocale } from "./error-messages";
import type { DaypartBand } from "@/domain/suggestions";

/**
 * i18n for the suggestions strips (cart upsell, item upsell, home daypart
 * row). Every other string in the storefront is hardcoded pt-BR; this module
 * follows `error-messages.ts`'s pattern exactly (same `ErrorLocale`, same
 * active-locale singleton) rather than inventing a second mechanism — see
 * that file's own note on why the whole UI isn't localizable yet.
 */
export type SuggestionMessageKey =
  | "cartStripTitle"
  | "itemStripTitle"
  | "daypartFallbackTitle"
  | "daypartBreakfastTitle"
  | "daypartLunchTitle"
  | "daypartAfternoonTitle"
  | "daypartDinnerTitle"
  | "daypartLateNightTitle"
  | "comboCta"
  | "addAriaLabel"
  | "addedAnnouncement";

const MESSAGES: Record<ErrorLocale, Record<SuggestionMessageKey, string>> = {
  "pt-BR": {
    cartStripTitle: "Quem pediu isso também levou",
    itemStripTitle: "Quem pediu isso também levou",
    daypartFallbackTitle: "Mais pedidos agora",
    daypartBreakfastTitle: "Para o café da manhã",
    daypartLunchTitle: "Para o almoço",
    daypartAfternoonTitle: "Para a tarde",
    daypartDinnerTitle: "Para o jantar",
    daypartLateNightTitle: "Para a madrugada",
    comboCta: "Leve o combo e economize {value}",
    addAriaLabel: "Adicionar {name}",
    addedAnnouncement: "Adicionado",
  },
  "en-US": {
    cartStripTitle: "Others who ordered this also got",
    itemStripTitle: "Others who ordered this also got",
    daypartFallbackTitle: "Popular right now",
    daypartBreakfastTitle: "For breakfast",
    daypartLunchTitle: "For lunch",
    daypartAfternoonTitle: "For the afternoon",
    daypartDinnerTitle: "For dinner",
    daypartLateNightTitle: "For late night",
    comboCta: "Get the combo and save {value}",
    addAriaLabel: "Add {name}",
    addedAnnouncement: "Added",
  },
  es: {
    cartStripTitle: "Quienes pidieron esto también llevaron",
    itemStripTitle: "Quienes pidieron esto también llevaron",
    daypartFallbackTitle: "Los más pedidos ahora",
    daypartBreakfastTitle: "Para el desayuno",
    daypartLunchTitle: "Para el almuerzo",
    daypartAfternoonTitle: "Para la tarde",
    daypartDinnerTitle: "Para la cena",
    daypartLateNightTitle: "Para la madrugada",
    comboCta: "Lleva el combo y ahorra {value}",
    addAriaLabel: "Agregar {name}",
    addedAnnouncement: "Agregado",
  },
};

const DAYPART_TITLE_KEY: Record<DaypartBand, SuggestionMessageKey> = {
  BREAKFAST: "daypartBreakfastTitle",
  LUNCH: "daypartLunchTitle",
  AFTERNOON: "daypartAfternoonTitle",
  DINNER: "daypartDinnerTitle",
  LATE_NIGHT: "daypartLateNightTitle",
};

/** Returns the localized text for a semantic message key, following the app's active locale. */
export function suggestionMessage(key: SuggestionMessageKey): string {
  return MESSAGES[getErrorLocale()][key];
}

/** Title for the home daypart row, by hour band. */
export function daypartRowTitle(band: DaypartBand): string {
  return suggestionMessage(DAYPART_TITLE_KEY[band]);
}

/** Substitutes `{name}`/`{value}` placeholders (one per message); money arrives preformatted by `formatBRL`. */
export function formatSuggestionMessage(key: SuggestionMessageKey, value: string): string {
  return suggestionMessage(key).replace(/\{[a-zA-Z]+\}/, value);
}
